/**
 * Shared UTM / click-id attribution resolution for form create and quote submit.
 */

export const TRACKING_PARAM_KEYS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "first_utm_source",
    "first_utm_medium",
    "first_utm_campaign",
    "first_utm_term",
    "gclid",
    "fbclid",
    "_fbc",
    "_fbp",
    "pysTrafficSource",
    "last_pysTrafficSource",
    "organic_source",
    "first_traffic_source",
    "handl_landing_page",
    "handl_original_ref",
    "handl_ref_domain",
    "traffic_source",
] as const;

export function normalizeTrackingParam(v: string | null): string {
    if (!v) return "";
    const trimmed = v.trim();
    if (!trimmed) return "";
    if (trimmed.toLowerCase() === "unknown") return "";
    if (trimmed.toLowerCase() === "(not set)") return "";
    return trimmed;
}

export function getUtmFromLandingPage(handlLandingPage: string, key: string): string {
    if (!handlLandingPage) return "";

    try {
        const url = new URL(handlLandingPage);
        const value = url.searchParams.get(key);
        if (!value) return "";
        if (value === "unknown" || value === "(not set)") return "";
        return value;
    } catch {
        return "";
    }
}

export function mergeTrackingParams(
    intoParams: URLSearchParams,
    incomingParams: URLSearchParams
): URLSearchParams {
    incomingParams.forEach((value, key) => {
        if (value && value !== "null" && value !== "undefined") {
            intoParams.set(key, value);
        }
    });
    return intoParams;
}

export function hasTrackingParamSignal(params: URLSearchParams): boolean {
    return TRACKING_PARAM_KEYS.some((k) => params.has(k));
}

const HANDL_URL_PARAM_KEYS = [
    "handl_landing_page",
    "handl_ref",
    "handl_original_ref",
    "handl_url",
] as const;

/** Lift tracking params embedded inside encoded handl_* URLs (tracking inference only). */
export function enrichQueryParamsFromHandlUrls(params: URLSearchParams): URLSearchParams {
    const enriched = new URLSearchParams(params);

    for (const handlKey of HANDL_URL_PARAM_KEYS) {
        const raw = params.get(handlKey);
        if (!raw) continue;

        try {
            const nested = new URL(raw).searchParams;
            for (const key of TRACKING_PARAM_KEYS) {
                if (normalizeTrackingParam(enriched.get(key))) continue;
                const value = nested.get(key);
                if (normalizeTrackingParam(value)) {
                    enriched.set(key, value!);
                }
            }
        } catch {
            // ignore malformed handl URLs
        }
    }

    return enriched;
}

export function resolveUtmCampaign(
    queryParams: URLSearchParams,
    handlLandingPage: string
): string {
    return (
        normalizeTrackingParam(queryParams.get("utm_campaign")) ||
        getUtmFromLandingPage(handlLandingPage, "utm_campaign") ||
        normalizeTrackingParam(queryParams.get("first_utm_campaign")) ||
        ""
    );
}

export function resolveUtmSource(
    queryParams: URLSearchParams,
    handlLandingPage: string
): string {
    return (
        normalizeTrackingParam(queryParams.get("utm_source")) ||
        getUtmFromLandingPage(handlLandingPage, "utm_source") ||
        normalizeTrackingParam(queryParams.get("first_utm_source")) ||
        ""
    );
}

export function resolveUtmMedium(
    queryParams: URLSearchParams,
    handlLandingPage: string
): string {
    return (
        normalizeTrackingParam(queryParams.get("utm_medium")) ||
        getUtmFromLandingPage(handlLandingPage, "utm_medium") ||
        normalizeTrackingParam(queryParams.get("first_utm_medium")) ||
        ""
    );
}

export interface IInferredAttribution {
    utmSource: string;
    utmMedium: string;
    utmCampaign: string;
    utmTerm: string;
    gclid: string;
    fbclid: string;
    fbc: string;
    fbp: string;
    hasGoogleClick: boolean;
    hasMetaClick: boolean;
    handlLandingPage: string;
    handlOriginalRef: string;
    handlRefDomain: string;
    pysTrafficSource: string;
    lastPysTrafficSource: string;
    organicSource: string;
    firstTrafficSource: string;
    isSEO: boolean;
    pys: string;
    source: string;
    medium: string;
    campaign: string;
    originalSource: string;
    originalMedium: string;
    originalCampaign: string;
    keyword: string;
    originalKeyword: string;
    trafficSource: string;
}

export function inferAttributionFromQueryParams(
    queryParams: URLSearchParams
): IInferredAttribution {
    const qp = enrichQueryParamsFromHandlUrls(queryParams);
    const handlLandingPage = normalizeTrackingParam(qp.get("handl_landing_page"));

    const utmSource = resolveUtmSource(qp, handlLandingPage);
    const utmMedium = resolveUtmMedium(qp, handlLandingPage);
    const utmCampaign = resolveUtmCampaign(qp, handlLandingPage);
    const utmTerm =
        normalizeTrackingParam(qp.get("utm_term")) ||
        getUtmFromLandingPage(handlLandingPage, "utm_term") ||
        normalizeTrackingParam(qp.get("first_utm_term")) ||
        "";

    const gclid = normalizeTrackingParam(qp.get("gclid"));
    const fbclid = normalizeTrackingParam(qp.get("fbclid"));
    const fbc = normalizeTrackingParam(qp.get("_fbc"));
    const fbp = normalizeTrackingParam(qp.get("_fbp"));

    const hasMetaClick = !!fbclid;
    const hasGoogleClick = !!gclid;

    const handlOriginalRef = normalizeTrackingParam(qp.get("handl_original_ref"));
    const handlRefDomain = normalizeTrackingParam(qp.get("handl_ref_domain"));

    const pysTrafficSource = normalizeTrackingParam(qp.get("pysTrafficSource"));
    const lastPysTrafficSource = normalizeTrackingParam(qp.get("last_pysTrafficSource"));
    const organicSource = normalizeTrackingParam(qp.get("organic_source"));
    const firstTrafficSource = normalizeTrackingParam(qp.get("first_traffic_source"));

    const seoEvidence = handlOriginalRef || organicSource;

    const isSearchEngineRef =
        /google\.|bing\.|yahoo\.|duckduckgo\.|baidu\.|yandex\.|search\.apple\.com|spotlight\.apple\.com/i.test(
            seoEvidence
        );

    const isSEO =
        !utmSource &&
        !utmMedium &&
        !utmCampaign &&
        !hasGoogleClick &&
        !hasMetaClick &&
        !!seoEvidence &&
        isSearchEngineRef;

    const pys = (pysTrafficSource || lastPysTrafficSource).toLowerCase();

    const source =
        utmSource ||
        (hasGoogleClick
            ? "google"
            : hasMetaClick
              ? "facebook"
              : isSEO
                ? "SEO"
                : pys.includes("facebook") || pys.includes("instagram")
                  ? "facebook"
                  : pys.includes("google")
                    ? "google"
                    : pys.includes("bing")
                      ? "bing"
                      : firstTrafficSource.toLowerCase() === "direct" || pys === "direct"
                        ? "direct"
                        : "UNKNOWN");

    const medium =
        utmMedium ||
        (hasGoogleClick
            ? "cpc"
            : hasMetaClick
              ? "paid_social"
              : isSEO
                ? "organic"
                : pys.includes("paid") || pys.includes("cpc")
                  ? "cpc"
                  : pys.includes("social") ||
                      pys.includes("facebook") ||
                      pys.includes("instagram")
                    ? "paid_social"
                    : firstTrafficSource.toLowerCase() === "direct" || pys === "direct"
                      ? "direct"
                      : "UNKNOWN");

    const campaign =
        utmCampaign ||
        (hasGoogleClick
            ? "google_ads"
            : hasMetaClick
              ? "meta"
              : isSEO
                ? "SEO"
                : pys || "UNKNOWN");

    const firstUtmCampaign = normalizeTrackingParam(qp.get("first_utm_campaign"));

    return {
        utmSource,
        utmMedium,
        utmCampaign,
        utmTerm,
        gclid,
        fbclid,
        fbc,
        fbp,
        hasGoogleClick,
        hasMetaClick,
        handlLandingPage,
        handlOriginalRef,
        handlRefDomain,
        pysTrafficSource,
        lastPysTrafficSource,
        organicSource,
        firstTrafficSource,
        isSEO,
        pys,
        source,
        medium,
        campaign,
        originalSource: normalizeTrackingParam(qp.get("first_utm_source")) || source,
        originalMedium: normalizeTrackingParam(qp.get("first_utm_medium")) || medium,
        originalCampaign: firstUtmCampaign || utmCampaign || "",
        keyword: utmTerm,
        originalKeyword: normalizeTrackingParam(qp.get("first_utm_term")) || utmTerm,
        trafficSource:
            normalizeTrackingParam(qp.get("traffic_source")) ||
            pysTrafficSource ||
            (isSEO ? "SEO" : hasMetaClick ? "meta" : hasGoogleClick ? "google_ads" : pys === "direct" ? "direct" : ""),
    };
}
